/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/CustomCalendar/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { useTranslation } from "react-i18next";
import styles from "./CustomCalendar.module.css";
import AppointmentRescheduleModal from "../../modals/AppointmentRescheduleModal";
import { getDynamicMonthDisplay } from "./customCalendarUtils.js";
import { useCustomCalendarWeek } from "./useCustomCalendarWeek.js";
import { useCustomCalendarDrag } from "./useCustomCalendarDrag.js";
import CustomCalendarHeader from "./CustomCalendarHeader.jsx";
import CustomCalendarGrid from "./CustomCalendarGrid.jsx";

const CustomCalendar = ({
  appointments = [],
  allAppointments = [], // All appointments for conflict detection
  onDateSelect,
  onAppointmentReschedule,
  eventData = null,
  participatingCompanyData = null, // Participating company data with tiempoBreak
  selectedDate = null,
  selectedTime = null,
  selectedMinute = null,
  senderAppointments = [],
  showConfirmButton = false,
  onConfirm = null,
  currentAppointment = null,
  isConfirmButtonDisabled = false,
  highlightedRequirementId = null,
  allowDragAllAppointments = true, // New prop to control drag behavior
  disableDragAndDrop = false, // New prop to completely disable drag and drop
}) => {
  const { t, i18n } = useTranslation();
  const {
    currentDate,
    weekDates,
    calendarGridRef,
    hourLabels,
    timeSlots,
    cellsPerHour,
    breakTimes,
    goToToday,
  } = useCustomCalendarWeek({
    eventData,
    participatingCompanyData,
    language: i18n.language,
  });
  const {
    draggedAppointment,
    dragOverTimeSlot,
    isRescheduleModalOpen,
    rescheduleData,
    isRescheduling,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleRescheduleConfirm,
    handleRescheduleCancel,
  } = useCustomCalendarDrag({
    appointments,
    allAppointments,
    currentAppointment,
    eventData,
    breakTimes,
    onAppointmentReschedule,
  });

  return (
    <div className={styles.calendar}>
      <CustomCalendarHeader
        monthDisplay={getDynamicMonthDisplay(
          eventData,
          currentDate,
          i18n.language,
        )}
        onToday={goToToday}
        showConfirmButton={showConfirmButton}
        isConfirmButtonDisabled={isConfirmButtonDisabled}
        onConfirm={onConfirm}
        t={t}
      />
      <CustomCalendarGrid
        calendarGridRef={calendarGridRef}
        weekDates={weekDates}
        timeSlots={timeSlots}
        hourLabels={hourLabels}
        cellsPerHour={cellsPerHour}
        language={i18n.language}
        t={t}
        appointments={appointments}
        senderAppointments={senderAppointments}
        currentAppointment={currentAppointment}
        highlightedRequirementId={highlightedRequirementId}
        selectedDate={selectedDate}
        selectedTime={selectedTime}
        selectedMinute={selectedMinute}
        draggedAppointment={draggedAppointment}
        dragOverTimeSlot={dragOverTimeSlot}
        disableDragAndDrop={disableDragAndDrop}
        allowDragAllAppointments={allowDragAllAppointments}
        breakTimes={breakTimes}
        eventData={eventData}
        allAppointments={allAppointments}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onDateSelect={onDateSelect}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      />
      <AppointmentRescheduleModal
        open={isRescheduleModalOpen}
        onClose={handleRescheduleCancel}
        onConfirm={handleRescheduleConfirm}
        appointment={rescheduleData?.appointment}
        newDate={rescheduleData?.newDate}
        newTime={rescheduleData?.newTime}
        eventData={eventData}
        isLoading={isRescheduling}
      />
    </div>
  );
};

export default CustomCalendar;
